/**

 * Product block shell layouts — stored in MongoDB on each popup template.

 *

 * - `generalSettings.productShellLayout` — template default (set in MongoDB / normalize)

 * - `product.setting.layout.shell` — per PRODUCT block (synced by ensureProductShellLayout)

 *

 * NOTE: Do not import product-recommendation-popup-schema.js (circular TDZ).

 */



/** @readonly */

export const PRODUCT_SHELL_LAYOUT = Object.freeze({

  VERTICAL: "vertical-v1",

  HORIZONTAL: "horizontal-v1",

  EXTENSION_COMPACT: "extension-compact-v1",

  /**
   * Thank-you / order-status list Layout 2 — image | title | price | CTA in one
   * row; variant + quantity controls below (Shopify product-upsell style).
   */
  EXTENSION_LIST_V2: "extension-list-v2",

  /** Multi-product horizontal carousel on checkout / thank-you / order-status */
  EXTENSION_SLIDER: "extension-slider-v1",

  EXTENSION_TWO_COLUMN: "extension-two-column-v1",

  /** Static custom cart drawer — image | details | CTA in one row */

  CART_DRAWER_HORIZONTAL: "cart-drawer-horizontal-v1",

  /** Static custom cart drawer — compact stacked cards in a carousel */

  CART_DRAWER_VERTICAL: "cart-drawer-vertical-v1",

  /** Product page — horizontal rows with checkboxes, stacked per slide */

  CART_DRAWER_CHECKBOX_STACK: "cart-drawer-checkbox-stack-v1",

  /** Product page — FBT vertical stack with claim-offer bundle CTA */

  CART_DRAWER_FBT_BUNDLE: "cart-drawer-fbt-bundle-v1",

});



const VALID_SHELLS = new Set(Object.values(PRODUCT_SHELL_LAYOUT));



/** @deprecated Alias for {@link PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL}. */

export const LEGACY_CART_DRAWER_HORIZONTAL_SHELL =
  PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL;



const EXTENSION_POPUP_TYPES = new Set([

  "checkout_extension_upsell_widget",

  "post_purchase_widget",

  "thankyou_widget",

  "order_status_widget",

  "cart_drawer_widget",

]);



export function isExtensionProductPopup(popup) {

  if (!popup || typeof popup !== "object") return false;

  if (EXTENSION_POPUP_TYPES.has(String(popup.popUpType || ""))) return true;

  const primary = String(popup?.category?.primary || "").toLowerCase();

  const key = String(popup?.category?.key || "").toLowerCase();

  return primary.includes("checkout extension") || key === "checkout-extensions";

}



function isValidProductShell(shell) {

  return typeof shell === "string" && VALID_SHELLS.has(shell);

}



function normalizeCartDrawerUpsellShell(shell) {
  if (shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL) {
    return PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL;
  }
  return shell;
}



/** Read popup-level shell from MongoDB `generalSettings.productShellLayout`. */

export function readPopupProductShellLayout(popup) {

  const shell = popup?.generalSettings?.productShellLayout;

  if (isValidProductShell(shell)) return shell;

  if (isExtensionProductPopup(popup)) {

    const popUpType = String(popup?.popUpType || "");

    if (popUpType === "post_purchase_widget") {

      return PRODUCT_SHELL_LAYOUT.EXTENSION_TWO_COLUMN;

    }

    return PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT;

  }

  return PRODUCT_SHELL_LAYOUT.VERTICAL;

}



export function shellToLegacyLayoutFields(shell) {

  if (shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL) {
    return {
      shell,
      imagePosition: "top",
      density: "compact",
    };
  }

  if (shell === PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER) {
    return {
      shell,
      imagePosition: "top",
      density: "compact",
    };
  }

  if (
    shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL ||
    shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_CHECKBOX_STACK ||
    shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_FBT_BUNDLE ||
    shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL ||
    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT ||
    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2 ||
    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_TWO_COLUMN
  ) {

    return {

      shell,

      imagePosition: "left",

      density: "standard",

    };

  }

  return {

    shell: PRODUCT_SHELL_LAYOUT.VERTICAL,

    imagePosition: "top",

    density: "vertical",

  };

}



/**

 * Runtime: read `product.setting.layout.shell`, else popup `generalSettings.productShellLayout`.

 * @param {object} [product] PRODUCT section

 * @param {object} [popup]

 */

export function resolveProductShellLayout(product, popup) {

  const fromProduct = product?.setting?.layout?.shell;

  if (isValidProductShell(fromProduct)) return fromProduct;

  if (popup) return readPopupProductShellLayout(popup);

  return PRODUCT_SHELL_LAYOUT.VERTICAL;

}



export function isHorizontalProductShell(shell) {
  return (
    shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL ||
    shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL ||
    shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_CHECKBOX_STACK ||
    shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_FBT_BUNDLE
  );
}



export function isCartDrawerVerticalShell(shell) {

  return shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL;

}



export function isCartDrawerCheckboxStackShell(shell) {

  return shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_CHECKBOX_STACK;

}



export function isCartDrawerFbtBundleShell(shell) {

  return shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_FBT_BUNDLE;

}



export function isCartDrawerBundleSelectionShell(shell) {

  return isCartDrawerCheckboxStackShell(shell) || isCartDrawerFbtBundleShell(shell);

}



/**

 * Resolve upsell product shell for static custom cart drawer.

 * @param {object} [popup]

 * @param {object} [productSection] PRODUCT block in drawer schema

 */

export function resolveCartDrawerUpsellShell(popup, productSection) {

  const fromProduct = normalizeCartDrawerUpsellShell(

    productSection?.setting?.layout?.shell

  );

  if (isValidProductShell(fromProduct)) return fromProduct;

  const fromGs = normalizeCartDrawerUpsellShell(

    popup?.generalSettings?.cartDrawerUpsellShell

  );

  if (isValidProductShell(fromGs)) return fromGs;

  return PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL;
}



export function isExtensionProductShell(shell) {

  return (

    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_COMPACT ||

    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_LIST_V2 ||

    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER ||

    shell === PRODUCT_SHELL_LAYOUT.EXTENSION_TWO_COLUMN

  );

}



/** Copy `generalSettings.productShellLayout` → `product.setting.layout.shell`. */

export function ensureProductShellLayout(product, popup) {

  if (!product || typeof product !== "object") return;

  product.setting = product.setting || {};

  const layout = product.setting.layout || {};

  const shell = readPopupProductShellLayout(popup);

  product.setting.layout = {

    ...layout,

    ...shellToLegacyLayoutFields(shell),

    preset: layout.preset,

  };

  if (product.setting.layout.shell === PRODUCT_SHELL_LAYOUT.EXTENSION_SLIDER) {
    const raw = Number(product.setting.layout.itemsPerRow);
    product.setting.layout.itemsPerRow = Number.isFinite(raw)
      ? Math.min(4, Math.max(1, Math.round(raw)))
      : 3;
  }

}


